import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";

export default function StudyPlanSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [examDate, setExamDate] = useState<Date>();
  const [targetScore, setTargetScore] = useState([80]);
  const [hoursPerDay, setHoursPerDay] = useState([2]);
  const [preferredTime, setPreferredTime] = useState("flexible");
  const [intensity, setIntensity] = useState("moderate");
  const [includeRestDays, setIncludeRestDays] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: weakAreas } = useQuery({
    queryKey: ['weak-areas', user?.id],
    queryFn: async () => {
      const { data: attempts } = await supabase
        .from('attempts')
        .select(`
          is_correct,
          mcqs!inner (
            topics!inner (
              id,
              name
            )
          )
        `)
        .eq('user_id', user!.id);

      const topicStats = new Map<string, { name: string; total: number; correct: number }>();
      
      attempts?.forEach((attempt: any) => {
        const topicId = attempt.mcqs?.topics?.id;
        const topicName = attempt.mcqs?.topics?.name;
        if (!topicId) return;

        const existing = topicStats.get(topicId);
        if (existing) {
          existing.total += 1;
          if (attempt.is_correct) existing.correct += 1;
        } else {
          topicStats.set(topicId, {
            name: topicName,
            total: 1,
            correct: attempt.is_correct ? 1 : 0
          });
        }
      });

      return Array.from(topicStats.values())
        .map(t => ({ ...t, accuracy: Math.round((t.correct / t.total) * 100) }))
        .filter(t => t.accuracy < 70)
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 5);
    },
    enabled: !!user
  });

  const { data: strongAreas } = useQuery({
    queryKey: ['strong-areas', user?.id],
    queryFn: async () => {
      const { data: attempts } = await supabase
        .from('attempts')
        .select(`
          is_correct,
          mcqs!inner (
            topics!inner (
              id,
              name
            )
          )
        `)
        .eq('user_id', user!.id);

      const topicStats = new Map<string, { name: string; total: number; correct: number }>();
      
      attempts?.forEach((attempt: any) => {
        const topicId = attempt.mcqs?.topics?.id;
        const topicName = attempt.mcqs?.topics?.name;
        if (!topicId) return;

        const existing = topicStats.get(topicId);
        if (existing) {
          existing.total += 1;
          if (attempt.is_correct) existing.correct += 1;
        } else {
          topicStats.set(topicId, {
            name: topicName,
            total: 1,
            correct: attempt.is_correct ? 1 : 0
          });
        }
      });

      return Array.from(topicStats.values())
        .map(t => ({ ...t, accuracy: Math.round((t.correct / t.total) * 100) }))
        .filter(t => t.accuracy >= 85)
        .sort((a, b) => b.accuracy - a.accuracy)
        .slice(0, 3);
    },
    enabled: !!user
  });

  const handleGeneratePlan = async () => {
    if (!examDate) {
      toast.error("Please select an exam date");
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-study-plan', {
        body: {
          examDate: examDate.toISOString(),
          targetScore: targetScore[0],
          hoursPerDay: hoursPerDay[0],
          preferences: {
            preferredTime,
            includeRestDays,
            intensityLevel: intensity
          }
        }
      });

      if (error) throw error;

      toast.success("Study plan generated successfully!");
      navigate(`/study-plan?planId=${data.planId}`);
    } catch (error) {
      console.error('Error generating study plan:', error);
      toast.error("Failed to generate study plan. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Create Your Personalized Study Plan</h1>
        <p className="text-muted-foreground">
          Let AI create a customized weekly schedule based on your goals and performance
        </p>
      </div>

      <div className="space-y-6">
        {/* Exam Details */}
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Exam Details</CardTitle>
            <CardDescription>Set your target exam date and score</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Target Exam Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !examDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {examDate ? format(examDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={examDate}
                    onSelect={setExamDate}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Target Score</Label>
                <span className="text-sm font-medium">{targetScore}%</span>
              </div>
              <Slider
                value={targetScore}
                onValueChange={setTargetScore}
                min={70}
                max={100}
                step={5}
              />
            </div>
          </CardContent>
        </Card>

        {/* Study Availability */}
        <Card>
          <CardHeader>
            <CardTitle>Step 2: Study Availability</CardTitle>
            <CardDescription>Configure your daily study schedule</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Hours per Day</Label>
                <span className="text-sm font-medium">{hoursPerDay} hours</span>
              </div>
              <Slider
                value={hoursPerDay}
                onValueChange={setHoursPerDay}
                min={1}
                max={8}
                step={0.5}
              />
            </div>

            <div className="space-y-2">
              <Label>Preferred Study Time</Label>
              <Select value={preferredTime} onValueChange={setPreferredTime}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">Morning</SelectItem>
                  <SelectItem value="afternoon">Afternoon</SelectItem>
                  <SelectItem value="evening">Evening</SelectItem>
                  <SelectItem value="flexible">Flexible</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Intensity Level</Label>
              <Select value={intensity} onValueChange={setIntensity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light (Relaxed pace)</SelectItem>
                  <SelectItem value="moderate">Moderate (Balanced)</SelectItem>
                  <SelectItem value="intensive">Intensive (Aggressive)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="rest-days">Include Rest Days</Label>
              <Switch
                id="rest-days"
                checked={includeRestDays}
                onCheckedChange={setIncludeRestDays}
              />
            </div>
          </CardContent>
        </Card>

        {/* Performance Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Step 3: Current Performance</CardTitle>
            <CardDescription>Auto-loaded from your practice history</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {weakAreas && weakAreas.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-destructive" />
                  Weak Areas Identified
                </h4>
                <div className="space-y-2">
                  {weakAreas.map((area, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{area.name}</span>
                      <span className="text-destructive font-medium">{area.accuracy}% accuracy</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {strongAreas && strongAreas.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  Strong Areas
                </h4>
                <div className="space-y-2">
                  {strongAreas.map((area, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{area.name}</span>
                      <span className="text-green-600 font-medium">{area.accuracy}% accuracy</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate(-1)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleGeneratePlan} disabled={isGenerating} className="flex-1">
            {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Generate Study Plan
          </Button>
        </div>
      </div>
    </div>
  );
}
